import _ from 'lodash'

import type { ServiceTopology } from '@wix/viewer-manager-interface'
import type { IComponentsRegistrar } from '@wix/thunderbolt-components-loader'
import type { ComponentsPreviewMappers } from '@wix/thunderbolt-becky-root'

import { registry, IThunderboltPreviewComponentsRegistry } from '@wix/editor-elements-registry/2.0/preview'
import { getComponentsLibraries } from '@wix/editor-elements-registry/2.0/toolbox'

import { IComponentsCommonRegistry, createCommonRegistryMethods, getComponentType } from './common'

/**
 * Preview Components Registry for Thunderbolt Client Side Rendering
 */
export type IComponentsPreviewRegistryCSR = {
	getRegistryAPI: () => IThunderboltPreviewComponentsRegistry
	getComponentsMappers: () => Promise<ComponentsPreviewMappers>
	getComponentsRegistrarAPI: () => IComponentsRegistrar
} & IComponentsCommonRegistry

const METRIC_CREATE_REGISTRY = 'create_registry_preview'

export async function createComponentsPreviewRegistryCSR(options: {
	serviceTopology?: ServiceTopology
	url?: string
	useNewStatics?: boolean
	experimentalMobileLibrary?: boolean
	runAndReport: <T>(metric: string, fn: () => Promise<T>) => Promise<T>
}): Promise<IComponentsPreviewRegistryCSR> {
	const { serviceTopology, url, runAndReport, useNewStatics, experimentalMobileLibrary } = options

	const libraries = serviceTopology
		? getComponentsLibraries({
				url,
				useNewStatics,
				experimentalMobileLibrary,
				serviceTopology: serviceTopology as any,
		  })
		: []

	const registryAPI = await runAndReport(METRIC_CREATE_REGISTRY, () =>
		registry({
			mode: 'eager',
			libraries,
			options: {
				useScriptsInsteadOfEval: true,
			},
		})
	)

	const commonRegistry = createCommonRegistryMethods(registryAPI)

	return {
		...commonRegistry,
		async getComponentsMappers() {
			const models = await registryAPI.loadAllComponents()
			return _.uniqBy(
				Object.entries(models)
					.filter(([, model]) => model.previewMapper || model.mapper)
					.map(
						([componentName, model]) => [getComponentType(componentName), model] as [string, typeof model]
					),
				([componentType]) => componentType
			).reduce<ComponentsPreviewMappers>((acc, [componentType, model]) => {
				return {
					...acc,
					[componentType]: {
						...(model.mapper || {}),
						...(model.previewMapper || {}),
					},
				}
			}, {})
		},

		getComponentsRegistrarAPI() {
			return {
				getComponents() {
					return registryAPI.getComponentsLoaders()
				},
			}
		},

		getRegistryAPI() {
			return registryAPI
		},
	}
}
